import { Banner } from "@apis/index";
import { deletesvg, edit, switchOff, switchOn } from "@assets/icons";
import Column from "@components/column";
import DeleteModal from "@components/delete-modal";
import Svg from "@components/svg";
import Table from "@components/table";
import { useModal } from "@context/ModalContext";
import useConfig from "@hooks/useConfig";
import { RootState, store } from "@store/index";
import { Helper } from "@utils/index";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const BannerTable = ({
  moduleId,
  isHome,
}: {
  moduleId?: number;
  isHome?: boolean;
}) => {
  const { data: user } = useSelector((state: RootState) => state.user);
  const { bucketUrl } = useConfig();
  const { showModal } = useModal();
  const navigate = useNavigate();

  const {
    items: banners,
    totalPages,
    totalItems,
  } = useSelector((state: RootState) => state[user.role + "Banners"]);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchBanners = useCallback(async () => {
    setLoading(true);

    setError(null);
    try {
      await Banner.list({
        state: user.role + "Banners",
        params: {
          page,
          limit: 30,
          ...(user.role === "store" && { storeId: user?.id }),
          role: user.role,
          moduleId: moduleId && moduleId,
          ...(isHome && { isHome: true }),
          ...(search && { search }),
        },
      });
    } catch (err) {
      console.error("Failed to fetch banners:", err);
      setError("Failed to load banners. Please check your network.");
    } finally {
      setLoading(false);
    }
  }, [moduleId, page, user.role, isHome, search]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const columns = useMemo(
    () => [
      {
        title: "SN",
        key: "sn",
        render: (_item: any, index: number) => index,
      },
      {
        title: "Title",
        key: "title",
        render: (item: any) => item.title,
      },
      {
        title: "Media",
        key: "images",
        render: (item: any) => (
          <div style={{ display: "grid", placeItems: "center" }}>
            <div
              style={{
                gap: 4,
                maxWidth: "170px",
                height: "70px",
                overflow: "scroll",
                display: "flex",
              }}
            >
              {item?.images?.length > 0 ? (
                item.images.map((img: any, idx: number) => {
                  if (img.videoUrl) {
                    return (
                      <div
                        key={img.id}
                        style={{
                          position: "relative",
                          minWidth: "80px",
                          height: "100%",
                        }}
                      >
                        <video
                          src={`${bucketUrl}/${img.videoUrl}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                          muted
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(0,0,0,0.5)",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 12,
                          }}
                        >
                          ▶
                        </div>
                      </div>
                    );
                  }
                  return (
                    <img
                      key={img.id}
                      loading="lazy"
                      src={`${bucketUrl}/${img.url}`}
                      alt="banner"
                      style={{
                        width: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  );
                })
              ) : (
                <p>No Media</p>
              )}
            </div>
          </div>
        ),
      },
      { title: "Type", key: "type", render: (item) => item.type },
      {
        title: "Zones",
        key: "zone",
        render: (item) => (
          <Column stylecolumn="gap:5px">
            {item?.zones?.length > 0 &&
              item.zones.map((z) => <p key={z.id}>{z.name}</p>)}
          </Column>
        ),
      },
      {
        title: "Is Active",
        key: "active",
        render: (item) => (
          <Svg
            src={item.isActive ? switchOn : switchOff}
            onClick={async () => {
              const formData = new FormData();
              const status = !item.isActive ? "true" : "false";
              formData.append("isActive", status);
              await Banner.updateStatus({
                state: user.role + "Banners",
                id: item.id,
                data: formData,
              }).then(() => {
                toast.success(
                  `Banner is set to ${!item.isActive ? "Active" : "Inactive"}`,
                );
              });
            }}
          />
        ),
      },
      {
        title: "Module",
        key: "module",
        render: (item: any) => item?.module?.name || "Home",
      },
    ],
    [user.role, fetchBanners, bucketUrl],
  );

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: 50, color: "red" }}>
        <p>{error}</p>
        <button onClick={fetchBanners} style={{ marginTop: 10 }}>
          Retry
        </button>
      </div>
    );
  }
  // const user = Helper.getUser();
  return (
    <Table
      loading={loading}
      text={loading ? "Loading banners..." : "Banner List"}
      columns={columns}
      data={banners}
      itemsPerPage={30}
      totalItems={totalItems}
      totalPages={totalPages}
      pageControlled={page}
      onPageChange={setPage}
      hasSearchbar={true}
      onSearchChange={setSearch}
      actions={[
        {
          key: "edit",
          icon: edit,
          onClick: (item) =>
            navigate(`/${user.role}/banner-settings/${item.id}`, {
              state: item,
            }),
        },
        {
          key: "remove",
          icon: deletesvg,
          onClick: async (item) => {
            showModal(
              <DeleteModal
                state={user.role + "Banners"}
                api={Banner}
                id={item.id}
                onSuccess={() => fetchBanners()}
              />,
            );
          },
        },
      ]}
    />
  );
};

export default BannerTable;
